/**
 * SearchSpring URL State Manager
 *
 * Bidirectional sync between URL search params and SS filter/sort/page state.
 * Uses SS-native param names: filter.{field}, sort.{field}, page
 *
 * This file is loaded as a standalone script tag by ss-collection.liquid.
 * It exposes SSURLState on window for use by ss-filters.js, ss-sorting.js, ss-pagination.js.
 */

(function () {
  'use strict';

  // URL params that must never be carried into SS state or back into the URL.
  // Collection scoping reaches SS only as the collection page's own
  // bgfilter.collection_handle; a filter.collection_handle arriving in the page
  // URL (on a collection page or on search) is dropped everywhere.
  const EXCLUDED_PARAMS = new Set(['filter.collection_handle']);

  const SSURLState = {
    /**
     * True for URL params that must never be sent to SS or written to the URL.
     */
    isExcludedParam(key) {
      return EXCLUDED_PARAMS.has(key);
    },

    /**
     * Remove excluded params from a URLSearchParams instance in place.
     */
    stripExcluded(searchParams) {
      EXCLUDED_PARAMS.forEach((key) => searchParams.delete(key));
      return searchParams;
    },

    /**
     * Read current URL params and return SS-compatible params object.
     * Handles multi-value filters (e.g., filter.size=M&filter.size=L).
     */
    readFromURL() {
      const url = new URL(window.location.href);
      const params = {};

      for (const [key, value] of url.searchParams) {
        if (!key.startsWith('filter.') && !key.startsWith('sort.') && key !== 'page' && key !== 'q') continue;
        if (SSURLState.isExcludedParam(key)) continue;

        if (params[key] !== undefined) {
          // Multi-value: convert to array
          if (Array.isArray(params[key])) {
            params[key].push(value);
          } else {
            params[key] = [params[key], value];
          }
        } else {
          params[key] = value;
        }
      }

      return params;
    },

    /**
     * Write SS params to URL via pushState.
     * Omits empty/default values. Preserves non-SS params (e.g., utm_*).
     */
    writeToURL(params) {
      const url = new URL(window.location.href);
      const searchParams = url.searchParams;

      // Remove all existing SS params
      const keysToDelete = [];
      for (const key of searchParams.keys()) {
        if (key.startsWith('filter.') || key.startsWith('sort.') || key === 'page') {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((k) => searchParams.delete(k));
      SSURLState.stripExcluded(searchParams);

      // Add new SS params
      for (const [key, value] of Object.entries(params)) {
        if (value === '' || value === null || value === undefined) continue;
        if (SSURLState.isExcludedParam(key)) continue;
        if (key === 'page' && (value === '1' || value === 1)) continue;

        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v));
        } else {
          searchParams.set(key, value);
        }
      }

      const newUrl = url.pathname + (searchParams.toString() ? '?' + searchParams.toString() : '');
      history.pushState({}, '', newUrl);
    },

    /**
     * Listen for browser back/forward and call callback with parsed params.
     */
    onPopState(callback) {
      window.addEventListener('popstate', () => {
        callback(SSURLState.readFromURL());
      });
    },
  };

  window.SSURLState = SSURLState;
})();
